import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import InventoryShell from "./InventoryShell";
import {
  Truck, Plus, ArrowLeft, ArrowUpRight, Check, AlertTriangle,
  Package, X, StickyNote,
} from "lucide-react";

/**
 * Receiving — Phase I4.
 *
 * Two surfaces in one file (router discriminates by URL):
 *   /operations/inventory/receiving        → landing (open + recent deliveries)
 *   /operations/inventory/receiving/:id    → line-by-line reconciliation wizard
 *
 * A "New delivery" dialog on the landing kicks off a Full or Partial delivery
 * with a picked vendor + PO reference and (optionally) pre-loaded SKU lines.
 */

const STATUS_STYLES = {
  pending:           { label: "Pending",             dot: "bg-stone-400",   pill: "bg-stone-100 text-stone-700" },
  receiving:         { label: "Receiving",           dot: "bg-amber-500",   pill: "bg-amber-50 text-amber-800" },
  discrepancy_open:  { label: "Variances open",  dot: "bg-rose-500",    pill: "bg-rose-50 text-rose-800" },
  reconciled:        { label: "Reconciled",          dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-800" },
};

function money(n) {
  const v = Number(n || 0);
  return `$${Math.abs(v).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function ReceivingRoute() {
  const { deliveryId } = useParams();
  return deliveryId ? <ReceivingWizard deliveryId={deliveryId} /> : <ReceivingLanding />;
}

// ─────────────────────── Landing ─────────────────────────────────────────

function ReceivingLanding() {
  const [data, setData] = useState({ deliveries: [], counts: { open: 0, recent: 0 } });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/deliveries");
      setData(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <InventoryShell
      title="Receiving"
      right={
        <button
          onClick={() => setShowNew(true)}
          data-testid="receiving-new-btn"
          className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-2"
        >
          <Plus size={13} strokeWidth={1.5} /> New delivery
        </button>
      }
    >
      {showNew && (
        <NewDeliveryDialog
          onCreate={async (delivery) => { setShowNew(false); navigate(`/operations/inventory/receiving/${delivery.id}`); }}
          onClose={() => setShowNew(false)}
        />
      )}

      {loading ? (
        <div className="text-sm text-stone-400">Loading…</div>
      ) : data.deliveries.length === 0 ? (
        <div className="bg-white subtle-border rounded-3xl p-10 text-sm text-stone-500 text-center" data-testid="receiving-empty">
          No deliveries yet. When one arrives, start a new delivery to reconcile it.
        </div>
      ) : (
        <div className="space-y-4" data-testid="receiving-list">
          {data.counts.open > 0 && (
            <div className="text-xs text-stone-500">
              {data.counts.open} open · {data.counts.recent} recent
            </div>
          )}
          <ul className="space-y-2">
            {data.deliveries.map((d) => {
              const s = STATUS_STYLES[d.status] || STATUS_STYLES.pending;
              return (
                <li key={d.id}>
                  <Link
                    to={`/operations/inventory/receiving/${d.id}`}
                    data-testid={`receiving-item-${d.id}`}
                    className="flex items-center gap-4 bg-white subtle-border rounded-2xl p-4 hover:bg-stone-50 transition-colors"
                  >
                    <div className={`w-9 h-9 rounded-2xl bg-stone-50 flex items-center justify-center text-stone-600`}>
                      <Truck size={14} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-900">
                        {d.vendor || "Delivery"} <span className="text-stone-400">·</span>{" "}
                        <span className="text-stone-500">{d.po_reference || "no PO"}</span>
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        {d.kind === "full" ? "Full delivery" : "Partial delivery"} · created {fmtDate(d.created_at)}
                      </div>
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${s.pill}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </div>
                    <ArrowUpRight size={14} className="text-stone-300 shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </InventoryShell>
  );
}

// ─────────────────────── New Delivery dialog ─────────────────────────────

function NewDeliveryDialog({ onCreate, onClose }) {
  const [kind, setKind] = useState("full");
  const [vendor, setVendor] = useState("");
  const [po, setPo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    setErrorMsg("");
    try {
      const r = await api.post("/inventory/deliveries", {
        kind,
        vendor: vendor.trim() || null,
        po_reference: po.trim() || null,
        notes: notes.trim() || null,
        lines: [],
      });
      onCreate(r.data.delivery);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not create delivery.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-stone-900/30 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="bg-white rounded-3xl subtle-border w-full max-w-md p-6" data-testid="receiving-new-dialog">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-xl text-stone-900">New delivery</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="receiving-new-close" className="text-stone-400 hover:text-stone-800">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-xs text-stone-700 mb-1.5">Type</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setKind("full")}
                data-testid="receiving-kind-full"
                className={`text-sm px-3 py-3 rounded-2xl subtle-border text-left ${kind === "full" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 hover:bg-stone-50"}`}
              >
                <div className="font-medium">Full delivery</div>
                <div className={`text-[11px] ${kind === "full" ? "text-white/70" : "text-stone-500"}`}>Complete against the packing slip</div>
              </button>
              <button
                type="button"
                onClick={() => setKind("partial")}
                data-testid="receiving-kind-partial"
                className={`text-sm px-3 py-3 rounded-2xl subtle-border text-left ${kind === "partial" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 hover:bg-stone-50"}`}
              >
                <div className="font-medium">Partial delivery</div>
                <div className={`text-[11px] ${kind === "partial" ? "text-white/70" : "text-stone-500"}`}>Subset — more to come</div>
              </button>
            </div>
          </div>
          <label className="block">
            <div className="text-xs text-stone-700 mb-1.5">Vendor</div>
            <input value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="receiving-new-vendor"
              className="w-full px-3 py-2 rounded-xl subtle-border text-sm outline-none" placeholder="e.g. NOW Wholesale" />
          </label>
          <label className="block">
            <div className="text-xs text-stone-700 mb-1.5">PO / packing slip reference</div>
            <input value={po} onChange={(e) => setPo(e.target.value)} data-testid="receiving-new-po"
              className="w-full px-3 py-2 rounded-xl subtle-border text-sm outline-none" placeholder="Optional" />
          </label>
          <label className="block">
            <div className="text-xs text-stone-700 mb-1.5">Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="receiving-new-notes"
              className="w-full px-3 py-2 rounded-xl subtle-border text-sm outline-none" />
          </label>
          {errorMsg && <div className="text-xs text-rose-700">{errorMsg}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} data-testid="receiving-new-submit"
              className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 flex-1">
              {saving ? "Creating…" : "Create delivery"}
            </button>
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-full subtle-border hover:bg-stone-50">
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────── Wizard (line reconciliation) ───────────────────

function ReceivingWizard({ deliveryId }) {
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inventory/deliveries/${deliveryId}`);
      setDelivery(r.data.delivery);
      setLines(r.data.lines);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not load delivery.");
    } finally { setLoading(false); }
  }, [deliveryId]);
  useEffect(() => { load(); }, [load]);

  const updateLine = async (lineId, patch) => {
    try {
      const r = await api.patch(`/inventory/deliveries/${deliveryId}/lines/${lineId}`, patch);
      setLines((prev) => prev.map((ln) => ln.id === lineId ? r.data : ln));
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not save line.");
    }
  };

  const addLine = async (productId, expected) => {
    try {
      await api.post(`/inventory/deliveries/${deliveryId}/lines`, { product_id: productId, expected_qty: expected });
      setShowAdd(false);
      await load();
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not add line.");
    }
  };

  const confirm = async () => {
    setConfirming(true);
    setErrorMsg("");
    try {
      const r = await api.post(`/inventory/deliveries/${deliveryId}/confirm`, {});
      // stay on the page so the GM sees the outcome; refresh to show reconciled status
      setDelivery(r.data.delivery);
      // toast-lite via error slot with positive vibe
      setErrorMsg("");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not confirm.");
    } finally { setConfirming(false); }
  };

  const stats = useMemo(() => {
    const total = lines.length;
    const touched = lines.filter((ln) => ln.received_qty !== null && ln.received_qty !== undefined).length;
    const variances = lines.filter((ln) => ln.variance_flag).length;
    return { total, touched, variances };
  }, [lines]);

  const isTerminal = delivery?.status === "reconciled" || delivery?.status === "discrepancy_open";

  if (loading) {
    return <InventoryShell title="Receiving"><div className="text-sm text-stone-400">Loading…</div></InventoryShell>;
  }
  if (!delivery) {
    return <InventoryShell title="Receiving"><div className="text-sm text-stone-500">{errorMsg || "Not found."}</div></InventoryShell>;
  }

  const s = STATUS_STYLES[delivery.status] || STATUS_STYLES.pending;

  return (
    <InventoryShell
      title="Receiving"
      right={
        <button
          onClick={() => navigate("/operations/inventory/receiving")}
          className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
          data-testid="receiving-back-to-landing"
        >
          <ArrowLeft size={12} /> All deliveries
        </button>
      }
    >
      <div className="bg-white subtle-border rounded-3xl p-5 sm:p-6 mb-5" data-testid="receiving-header">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] label-eyebrow text-stone-400">
              {delivery.boutique_code} · {delivery.kind === "full" ? "Full delivery" : "Partial delivery"}
            </div>
            <h2 className="font-serif-display text-stone-900 text-2xl mt-1">
              {delivery.vendor || "Delivery"}{delivery.po_reference ? ` · ${delivery.po_reference}` : ""}
            </h2>
            {delivery.notes && (
              <p className="text-xs text-stone-500 mt-2 flex items-start gap-1">
                <StickyNote size={11} className="mt-0.5 text-stone-400" />
                {delivery.notes}
              </p>
            )}
          </div>
          <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full ${s.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {s.label}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <MiniStat label="SKUs" value={stats.total} testid="receiving-stat-total" />
          <MiniStat label="Received" value={`${stats.touched} / ${stats.total}`} testid="receiving-stat-touched" />
          <MiniStat label="Variances" value={stats.variances} tone={stats.variances > 0 ? "amber" : "calm"} testid="receiving-stat-disc" />
        </div>
      </div>

      {errorMsg && (
        <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 mb-4 flex items-start gap-2" data-testid="receiving-error">
          <AlertTriangle size={14} className="mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {isTerminal ? (
        <div className="bg-white subtle-border rounded-3xl p-6 text-sm text-stone-700" data-testid="receiving-terminal">
          <div className="flex items-center gap-2 mb-2">
            <Check size={14} className="text-emerald-600" />
            <span className="font-medium text-stone-900">Delivery {delivery.status === "reconciled" ? "reconciled" : "confirmed with variances"}.</span>
          </div>
          <p className="text-xs text-stone-500">
            On-hand quantities updated{stats.variances > 0 ? `. ${stats.variances} variance${stats.variances === 1 ? " is" : "s are"} waiting in Variance Review.` : "."}
          </p>
          {stats.variances > 0 && (
            <Link to="/operations/inventory/variances" className="mt-3 inline-flex items-center gap-1 text-xs text-stone-900 underline underline-offset-4">
              Go to Variance Review <ArrowUpRight size={11} />
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-stone-500">Line-by-line reconciliation</div>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              data-testid="receiving-add-line"
              className="text-xs px-3 py-1.5 rounded-full subtle-border hover:bg-stone-50 inline-flex items-center gap-1.5"
            >
              <Plus size={12} /> Add line
            </button>
          </div>

          {showAdd && <AddLineDialog onAdd={addLine} onClose={() => setShowAdd(false)} existingProductIds={lines.map((l) => l.product_id)} />}

          {lines.length === 0 ? (
            <div className="bg-white subtle-border rounded-2xl p-6 text-sm text-stone-500 text-center">
              No lines yet. Add SKUs from the delivery to reconcile.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="receiving-line-list">
              {lines.map((ln) => (
                <LineReconciliationCard key={ln.id} line={ln} onSave={updateLine} />
              ))}
            </ul>
          )}

          {lines.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-[11px] text-stone-500">
                Confirming updates on-hand quantities. Any variances flow into Variance Review.
              </div>
              <button
                type="button"
                onClick={confirm}
                disabled={confirming || stats.touched < stats.total}
                data-testid="receiving-confirm"
                className="text-sm px-5 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40"
              >
                {confirming ? "Confirming…" : "Confirm delivery"}
              </button>
            </div>
          )}
        </>
      )}
    </InventoryShell>
  );
}

function MiniStat({ label, value, hint, tone = "calm", testid }) {
  const toneCls = tone === "amber" ? "text-amber-700" : "text-stone-900";
  return (
    <div className="rounded-2xl bg-stone-50/60 subtle-border px-4 py-3" data-testid={testid}>
      <div className="text-[10px] label-eyebrow text-stone-400">{label}</div>
      <div className={`mt-1 font-serif-display text-2xl ${toneCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-stone-400 mt-0.5">{hint}</div>}
    </div>
  );
}

// ─────────────────────── Line row (numeric autosave on blur) ────────────

function LineReconciliationCard({ line, onSave }) {
  const [received, setReceived] = useState(line.received_qty ?? "");
  const [damage, setDamage] = useState(line.damage_qty ?? "");
  const [notes, setNotes] = useState(line.notes ?? "");

  useEffect(() => {
    setReceived(line.received_qty ?? "");
    setDamage(line.damage_qty ?? "");
    setNotes(line.notes ?? "");
  }, [line.received_qty, line.damage_qty, line.notes]);

  const dirty = (
    String(received) !== String(line.received_qty ?? "") ||
    String(damage) !== String(line.damage_qty ?? "") ||
    (notes || "") !== (line.notes || "")
  );

  const commit = () => {
    if (!dirty) return;
    const patch = {};
    if (received !== "") patch.received_qty = Number(received);
    if (damage !== "") patch.damage_qty = Number(damage);
    if (notes !== (line.notes || "")) patch.notes = notes;
    onSave(line.id, patch);
  };

  const receivedNum = Number(received || 0);
  const damageNum = Number(damage || 0);
  const netVariance = (receivedNum - damageNum) - Number(line.expected_qty || 0);
  const flagged = line.received_qty !== null && (netVariance !== 0 || damageNum > 0);

  return (
    <li className={`bg-white subtle-border rounded-2xl p-4 ${flagged ? "ring-1 ring-amber-100" : ""}`} data-testid={`receiving-line-${line.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-1 self-stretch rounded-full ${flagged ? "bg-amber-300" : "bg-stone-200"}`} />
        <div className="w-9 h-9 rounded-2xl bg-stone-50 flex items-center justify-center text-stone-600">
          <Package size={14} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-stone-900">{line.product_display_name}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            SKU {line.product_sku} · expected {line.expected_qty}
          </div>
        </div>
        <label className="flex flex-col text-[10px] text-stone-500 items-center">
          Received
          <input
            type="number"
            min={0}
            value={received}
            onChange={(e) => setReceived(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`receiving-line-received-${line.id}`}
            className="w-20 mt-0.5 px-2 py-1.5 rounded-lg subtle-border text-sm text-center outline-none"
          />
        </label>
        <label className="flex flex-col text-[10px] text-stone-500 items-center">
          Damage
          <input
            type="number"
            min={0}
            value={damage}
            onChange={(e) => setDamage(e.target.value)}
            onBlur={commit}
            data-testid={`receiving-line-damage-${line.id}`}
            className="w-20 mt-0.5 px-2 py-1.5 rounded-lg subtle-border text-sm text-center outline-none"
          />
        </label>
      </div>
      {flagged && (
        <div className="mt-3 ml-8">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={commit}
            placeholder={damageNum > 0 ? "Explain the damage (required)" : "Add a note about the variance"}
            data-testid={`receiving-line-note-${line.id}`}
            className="w-full px-3 py-1.5 rounded-lg subtle-border text-xs outline-none"
          />
          <div className="text-[10px] text-amber-700 mt-1">
            {netVariance > 0 ? `Over by ${netVariance}` : netVariance < 0 ? `Short by ${Math.abs(netVariance)}` : ""}
            {damageNum > 0 && netVariance === 0 && `${damageNum} damaged of ${line.expected_qty}`}
          </div>
        </div>
      )}
    </li>
  );
}

// ─────────────────────── Add-line dialog ─────────────────────────────────

function AddLineDialog({ onAdd, onClose, existingProductIds = [] }) {
  const [products, setProducts] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [expected, setExpected] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/inventory/products")
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setProducts([]));
  }, []);

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return products
      .filter((p) => !existingProductIds.includes(p.id))
      .filter((p) => !q || p.display_name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      .slice(0, 12);
  }, [products, searchQ, existingProductIds]);

  const submit = async (product) => {
    setSaving(true);
    try { await onAdd(product.id, Number(expected) || 1); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-stone-900/30 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-3xl subtle-border w-full max-w-md p-6" data-testid="receiving-add-dialog">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-xl text-stone-900">Add SKU to delivery</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="receiving-add-close" className="text-stone-400 hover:text-stone-800">
            <X size={16} />
          </button>
        </div>
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          autoFocus
          placeholder="Search products…"
          data-testid="receiving-add-search"
          className="w-full px-3 py-2 rounded-xl subtle-border text-sm outline-none mb-3"
        />
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-stone-500">Expected qty</label>
          <input
            type="number"
            min={1}
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            data-testid="receiving-add-expected"
            className="w-20 px-2 py-1.5 rounded-lg subtle-border text-sm text-center outline-none"
          />
        </div>
        <ul className="max-h-72 overflow-y-auto space-y-1">
          {filtered.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => submit(p)}
                disabled={saving}
                data-testid={`receiving-add-pick-${p.id}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-stone-50 text-left disabled:opacity-40"
              >
                <span className="text-xs text-stone-400 w-14 shrink-0">{p.sku}</span>
                <span className="text-sm text-stone-800 flex-1">{p.display_name}</span>
                <span className="text-[10px] text-stone-400">on hand {p.current_qty}</span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-stone-400">
              No matching products.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
